import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import DepotMap from "../components/DepotMap";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { MapPin, Search, Info, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import EmployerLogo from "../components/EmployerLogo";

function fmtSalary(v) {
  if (v.salary_min && v.salary_max) return `£${v.salary_min.toLocaleString()}–£${v.salary_max.toLocaleString()}`;
  if (v.salary_min) return `From £${v.salary_min.toLocaleString()}`;
  return "Salary on application";
}

export default function DepotFinder() {
  const [postcode, setPostcode] = useState("");
  const [minutes, setMinutes] = useState(45);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!postcode.trim()) return;
    setLoading(true);
    try {
      const r = await api.get("/map/vacancies", { params: { postcode, minutes } });
      setResult(r.data);
      if (r.data.vacancies.length === 0) {
        toast.info(`No live vacancies within ${minutes} minutes of ${r.data.postcode}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Postcode lookup failed");
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-20">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-ink">Vacancy Map</div>
        <h1 className="font-display font-extrabold text-ink text-4xl md:text-5xl tracking-tight mt-2 leading-[1.1] max-w-3xl">
          Find every live UK rail vacancy within driving distance of your postcode.
        </h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Enter your UK postcode and choose a max drive time. We'll plot every live job pin on the map and rank them by driving time — Train Operating Companies, infrastructure managers, freight, contractors, the lot.
        </p>

        <form onSubmit={search} className="mt-8 grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-3 items-end" data-testid="finder-form">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">UK Postcode</label>
            <div className="relative mt-2">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="finder-postcode" className="pl-9 uppercase" placeholder="e.g. M1 2QF" value={postcode} onChange={(e) => setPostcode(e.target.value)} />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">Max drive ({minutes} min)</label>
            <div className="mt-3"><Slider value={[minutes]} min={15} max={120} step={5} onValueChange={(v) => setMinutes(v[0])} data-testid="finder-minutes" /></div>
          </div>
          <Button type="submit" disabled={loading} className="bg-navy text-bone hover:bg-lime-600 hover:text-ink h-11 px-6 font-semibold" data-testid="finder-submit">
            <Search className="w-4 h-4 mr-2" /> {loading ? "Searching…" : "Find vacancies"}
          </Button>
        </form>

        <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-2">
          <Info className="w-3.5 h-3.5" /> Postcodes via <a href="https://postcodes.io" target="_blank" rel="noreferrer" className="underline hover:text-ink">postcodes.io</a>; driving times via <a href="https://project-osrm.org" target="_blank" rel="noreferrer" className="underline hover:text-ink">OSRM</a>; map tiles © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer" className="underline hover:text-ink">OpenStreetMap</a>. Free, no API key — peak-hour traffic may add 15–30%.
        </div>

        <div className="mt-10 grid lg:grid-cols-[1fr_380px] gap-8">
          <DepotMap
            origin={result?.origin}
            depots={(result?.vacancies || []).map((v) => ({
              id: v.id,
              name: v.title,
              employer_name: v.employer_name,
              address: v.location,
              lat: v.lat,
              lng: v.lng,
              duration_minutes: v.duration_minutes,
              distance_km: v.distance_km,
            }))}
            allDepots={[]}
            minutes={minutes}
            height={560}
          />
          <aside className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
            {!result && (
              <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground">
                Enter a postcode to plot live vacancies and find your closest match.
              </div>
            )}
            {result && result.vacancies.length === 0 && (
              <div className="border border-dashed border-border rounded-lg p-8 text-center text-sm text-muted-foreground" data-testid="finder-empty">
                No live vacancies within {minutes} minutes of {result.postcode}. Try a longer drive time, a different postcode, or browse the <Link to="/vacancies" className="text-ink font-semibold underline">full job list</Link>.
              </div>
            )}
            {result?.vacancies.map((v) => (
              <Link
                key={v.id}
                to={`/vacancies/${v.id}`}
                className="block border border-border rounded-lg p-5 bg-card hover:border-amber transition-colors group"
                data-testid={`finder-vacancy-${v.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    <EmployerLogo name={v.employer_name} size={36} className="shrink-0 mt-0.5" />
                    <div className="min-w-0">
                      <div className="font-display font-bold text-ink line-clamp-2 group-hover:text-amber-700">{v.title}</div>
                      <div className="text-xs text-muted-foreground truncate">{v.employer_name} · {v.location}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono text-amber-700 font-bold">{v.duration_minutes} min</div>
                    <div className="text-[11px] text-muted-foreground">{v.distance_km} km</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <div className="text-[12px] font-mono text-ink truncate">{fmtSalary(v)}</div>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-700 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform shrink-0" />
                </div>
              </Link>
            ))}
          </aside>
        </div>
      </div>
    </div>
  );
}
